import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeFirestore, getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Ambient declaration to satisfy TS when Node types aren't resolved
declare const process: { env: Record<string, string | undefined> };

// Configuración desde variables de entorno CRA (prefijo REACT_APP_)
const firebaseConfig: Record<string, string | undefined> = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Inicialización segura: si faltan variables, no inicializa
let app;
let authInstance: ReturnType<typeof getAuth> | undefined;
let dbInstance: ReturnType<typeof initializeFirestore> | ReturnType<typeof getFirestore> | undefined;
let storageInstance: ReturnType<typeof getStorage> | undefined;
try {
  // Detectar placeholders comunes para evitar peticiones con claves incorrectas
  const apiKey = firebaseConfig.apiKey || '';
  const appId = firebaseConfig.appId || '';
  const isPlaceholderKey = apiKey.toUpperCase().includes('YOUR_FIREBASE_API_KEY');
  const isPlaceholderAppId = appId.toUpperCase().includes('YOUR_FIREBASE_APP_ID');
  const hasMinimalConfig = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);

  // Log sanitario para confirmar configuración sin exponer secretos completos
  try {
    console.info('[Firebase] Web config cargada', {
      projectId: firebaseConfig.projectId,
      authDomain: firebaseConfig.authDomain,
      appId: firebaseConfig.appId,
      apiKeyLast4: (apiKey || '').slice(-4),
    });
  } catch {}

  if (!hasMinimalConfig || isPlaceholderKey || isPlaceholderAppId) {
    console.error('[Firebase] Config inválida o placeholder en web. Revisa .env REACT_APP_FIREBASE_*', {
      projectId: firebaseConfig.projectId,
      appId: firebaseConfig.appId,
      apiKeyLast4: (apiKey || '').slice(-4),
      isPlaceholderKey,
      isPlaceholderAppId,
    });
  } else {
    // CRITICAL: Validate Storage Bucket
    if (firebaseConfig.storageBucket && !firebaseConfig.storageBucket.includes('firebasestorage.app')) {
      console.warn('[Firebase] CRITICAL WARNING: storageBucket might be incorrect. Expected domain ending in .firebasestorage.app', {
        current: firebaseConfig.storageBucket
      });
    }

    if (!getApps().length) {
      app = initializeApp(firebaseConfig as any);
      // Configure Firestore once with long polling for proxy-friendly behavior
      dbInstance = initializeFirestore(app, {
        experimentalForceLongPolling: true,
      });
    } else {
      app = getApp();
      // Reuse existing Firestore instance on HMR to avoid re-init errors
      dbInstance = getFirestore(app);
    }
    authInstance = getAuth(app);
    // Inicializa Storage con bucket explícito cuando sea posible
    try {
      const bucket = (firebaseConfig.storageBucket || '').trim();
      if (bucket) {
        const gsUrl = bucket.startsWith('gs://') ? bucket : `gs://${bucket}`;
        storageInstance = getStorage(app, gsUrl);
        try {
          console.info('[Firebase] Storage inicializado con bucket explícito', { bucket: gsUrl });
        } catch {}
      } else if (firebaseConfig.projectId) {
        const gsUrl = `gs://${firebaseConfig.projectId}.appspot.com`;
        storageInstance = getStorage(app, gsUrl);
        console.warn('[Firebase] storageBucket no definido, usando bucket por defecto basado en projectId', { bucket: gsUrl });
      } else {
        console.warn('[Firebase] No se pudo inicializar Storage: falta storageBucket y projectId');
      }
      // Nota: algunas versiones del SDK no exponen APIs para ajustar tiempos de reintento.
      // Si necesitas timeouts más agresivos, implementa cancelación manual con
      // `uploadBytesResumable` y `task.cancel()` o un `Promise.race` con timeout externo.
    } catch (se) {
      console.warn('[Firebase] Error inicializando Storage', se);
    }
    // Ensure session persists across reloads
    setPersistence(authInstance, browserLocalPersistence).catch((e) => {
      console.warn('[Firebase] No se pudo configurar persistencia de sesión', e);
    });
  }
} catch (e) {
  console.error('[Firebase] Error inicializando', e);
}

export const auth = authInstance ?? (undefined as any);
export const db = dbInstance ?? (undefined as any);
export const storage = storageInstance ?? (undefined as any);